// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// complianceHold (#352) — the ONE shared Compliance-Hold predicate.
//
// Compliance Hold = a candidate is barred from being scheduled on/after their
// Recruiter-set `Scheduling_Hold_Effective_Date` (Pam's field in FileMaker).
// Intent (Leland): when held, NO jobs can be scheduled for that installer, and
// the UI shows it's due to non-compliance.
//
// WHY THIS FILE EXISTS: the feature was enforced by FIVE separate inline checks
// (icon/marker in schedule.context, "UNAVAILABLE" in scheduleScreen ×2, the
// JobInitModal SW-710 assign-block, and the reassign-block in the WO/Repair
// confirm modals). They drifted — #174's later lock-cell intercept treated the
// compliance marker's `locked:true` as an *overridable* manual lock, so a job
// could be force-added to a held day while removal stayed blocked → stranded
// jobs (Mervin, JIRA-9523 / #352). Routing every path through this one pure
// predicate is exactly Mervin's ask: "all scheduler actions use the same
// Compliance Hold validation rules."
//
// Dates: `Scheduling_Hold_Effective_Date` is a locale "MM/DD/YYYY" string; the
// cell date is a weekDatesArray "M/D/YYYY" string. Compare date-only (midnight).
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

/**
 * Is this candidate on Compliance Hold for the given calendar day?
 *
 * @param {object|null|undefined} candidate a Candidates doc (needs
 *   `Scheduling_Hold_Effective_Date`); tolerant of null/absent.
 * @param {string} dateString the day to test (a weekDatesArray entry, e.g. "7/13/2026").
 * @returns {boolean} true iff the candidate has an effective date AND the day is
 *   on/after it. No effective date, or an unparseable date, → false (fail-open:
 *   an installer is never wrongly blocked because of a bad/blank hold value).
 */
export function isComplianceHeld(candidate, dateString) {
  const effRaw = candidate?.Scheduling_Hold_Effective_Date;
  if (!effRaw || !dateString) return false;

  const target = new Date(dateString);
  const effective = new Date(effRaw);
  if (Number.isNaN(target.getTime()) || Number.isNaN(effective.getTime())) {
    return false;
  }
  target.setHours(0, 0, 0, 0);
  effective.setHours(0, 0, 0, 0);
  return target >= effective;
}

// The alert shown when a job is dropped on a compliance-held installer. Kept
// verbatim from the original SW-710 hard-block (JobInitModal) — Leland's
// explicit call to keep the existing wording (#352).
export const COMPLIANCE_HOLD_ALERT =
  "Compliance Hold: Please contact your Regional Manager for assistance.";
